import {
  OpeningHours,
} from '../../../interfaces/delivery-company/pickups';
import {
  Card,
  Checkbox,
  Chip,
  Alert,
  alpha,
  Paper,
  Tooltip, IconButton, useTheme, useMediaQuery,
} from '@mui/material';
import {
  Box,
  Button,
  Stack,
  Typography,
  Divider,
} from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import React, { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DeleteOutline } from '@mui/icons-material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { isDesktop, isMobile } from 'react-device-detect';
const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;
type DayKey = (typeof DAYS)[number];
type Pair = [string, string];
type ScheduleEditorProps = {
  value: OpeningHours;                // your v2 shape
  onChange: (v: OpeningHours) => void;
  disabled?: boolean;
  onValidityChange?: (hasError: boolean) => void; // NEW
};
// ----- TIME HELPERS -----
const toMinutes = (hhmm24: string) => {
  if (!hhmm24) return NaN;
  const [h, m] = hhmm24.split(':').map(Number);
  return h * 60 + m;
};
const minutesTo24 = (min: number) => {
  const h = Math.floor(min / 60), m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};
const minutesTo12 = (min: number) => {
  let h = Math.floor(min / 60);
  const m = min % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, '0')} ${ampm}`;
};
const dateFrom24 = (hhmm: string | null): Date | null => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date();
  d.setHours(h ?? 0, m ?? 0, 0, 0);
  return d;
};
const to24FromDate = (d: Date | null): string => {
  if (!d) return '';
  return minutesTo24(d.getHours() * 60 + d.getMinutes());
};

export function ScheduleEditor({ value, onChange, disabled ,onValidityChange}: ScheduleEditorProps) {
  const theme = useTheme();
  const [bulkFrom, setBulkFrom] = useState<Date | null>(null);
  const [bulkTo, setBulkTo] = useState<Date | null>(null);
  const [selected, setSelected] = useState<Set<DayKey>>(new Set());
  const [globalError, setGlobalError] = useState('');

  // PER-DAY ERRORS + HIGHLIGHT
  const [dayError, setDayError] = useState<Record<DayKey, string>>({
    monday: '', tuesday: '', wednesday: '', thursday: '', friday: '', saturday: '', sunday: '',
  });
  const [errorDays, setErrorDays] = useState<Set<DayKey>>(new Set());

  // clear banner when nothing selected
  useEffect(() => {
    if (selected.size === 0 && globalError) setGlobalError('');
  }, [selected, globalError]);
  const dayHasIncomplete = (pairs: [string, string][]) =>
    pairs.some(([a, b]) => !a || !b);

// compute overall error state
  const computeHasError = useCallback(() => {
    const anyIncomplete = Object.values(value.schedule).some(dayHasIncomplete);
    return errorDays.size > 0 || anyIncomplete;
  }, [value.schedule, errorDays]);

// fire to parent whenever it may change
  useEffect(() => {
    onValidityChange?.(computeHasError());
  }, [computeHasError, onValidityChange]);

  const onBulkFromChange = (d: Date | null) => {
    setBulkFrom(d);
    if (globalError) setGlobalError('');
  };
  const onBulkToChange = (d: Date | null) => {
    setBulkTo(d);
    if (globalError) setGlobalError('');
  };

  const toggleDay = (d: DayKey) => {
    const next = new Set(selected);
    next.has(d) ? next.delete(d) : next.add(d);
    setSelected(next);
    if (globalError) setGlobalError('');
  };

  // ── VALIDATION ──
  function validateDay(day: DayKey, pairs: Pair[]): { ok: boolean; msg?: string } {
    // ignore incomplete rows in overlap checks, but validate complete rows strictly
    const complete = pairs
      .map(([a, b], idx) => ({ a, b, idx, am: toMinutes(a), bm: toMinutes(b) }))
      .filter(x => x.a && x.b && !Number.isNaN(x.am) && !Number.isNaN(x.bm));

    // each complete row must have a < b
    for (const row of complete) {
      if (!(row.am < row.bm)) {
        return {
          ok: false,
          msg: `On ${cap(day)}: start must be earlier than end (found ${minutesTo12(row.am)} – ${minutesTo12(row.bm)}).`,
        };
      }
    }

    // sort by start to check order/overlap
    const sorted = [...complete].sort((x, y) => x.am - y.am);
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1], cur = sorted[i];
      if (!(prev.bm < cur.am)) {
        return {
          ok: false,
          msg: `On ${cap(day)}: these periods conflict: ${minutesTo12(prev.am)}–${minutesTo12(prev.bm)} and ${minutesTo12(cur.am)}–${minutesTo12(cur.bm)}. Periods can’t overlap or touch.`,
        };
      }
    }
    return { ok: true };
  }

  function setPairs(day: DayKey, pairs: Pair[]) {
    // We’ll keep incomplete rows at the end; complete rows stay sorted by start
    const completes = pairs.filter(([a, b]) => a && b);
    const empties = pairs.filter(([a, b]) => !a || !b);

    // sort complete by start
    const withMins = completes
      .map(([a, b]) => ({ a, b, am: toMinutes(a), bm: toMinutes(b) }))
      .sort((x, y) => x.am - y.am)
      .map(({ a, b }) => [a, b] as Pair);
    const normalized = [...withMins, ...empties];

    const result = { ...value, schedule: { ...value.schedule, [day]: normalized } };
    const v = validateDay(day, normalized);

    if (v.ok) {
      onChange(result);
      setDayError(s => ({ ...s, [day]: '' }));
      setErrorDays(prev => {
        const n = new Set(prev);
        n.delete(day);
        return n;
      });
      if (errorDays.size === 0) setGlobalError('');
    } else {
      // still store user edits so they can fix inline
      onChange(result);
      setDayError(s => ({ ...s, [day]: v.msg! }));
      setErrorDays(prev => {
        const n = new Set(prev);
        n.add(day);
        return n;
      });
    }
  }

  const addEmptyRow = (day: DayKey) => {
    const next = [...(value.schedule[day] || []), ['', ''] as Pair];
    setPairs(day, next);
  };

  const deleteRow = (day: DayKey, idx: number) => {
    const next = (value.schedule[day] || []).filter((_, i) => i !== idx);
    setPairs(day, next);
  };

  const hasIncompleteRow = (pairs: Pair[]) => pairs.some(([a, b]) => !a || !b);

  // ── BULK ── add one editable row to each selected (complete times required)
  const addToSelected = () => {
    if (!bulkFrom || !bulkTo) return;
    const a = bulkFrom.getHours() * 60 + bulkFrom.getMinutes();
    const b = bulkTo.getHours() * 60 + bulkTo.getMinutes();
    if (!(a < b)) {
      setGlobalError('Start time must be earlier than end time.');
      return;
    }

    const pair: Pair = [minutesTo24(a), minutesTo24(b)];

    const failures: DayKey[] = [];
    const next = { ...value.schedule };

    selected.forEach(d => {
      const merged = [...(next[d] || []), pair];
      const v = validateDay(d, merged);
      if (v.ok) {
        // sort complete rows and keep incompletes last
        const complete = merged.filter(([sa, sb]) => sa && sb)
          .map(([sa, sb]) => ({ sa, sb, am: toMinutes(sa), bm: toMinutes(sb) }))
          .sort((x, y) => x.am - y.am)
          .map(({ sa, sb }) => [sa, sb] as Pair);
        const incomplete = merged.filter(([sa, sb]) => !sa || !sb);
        next[d] = [...complete, ...incomplete];
        setDayError(s => ({ ...s, [d]: '' }));
        setErrorDays(prev => {
          const n = new Set(prev);
          n.delete(d);
          return n;
        });
      } else {
        failures.push(d);
        next[d] = merged; // still reflect row
        setDayError(s => ({ ...s, [d]: v.msg! }));
        setErrorDays(prev => {
          const n = new Set(prev);
          n.add(d);
          return n;
        });
      }
    });

    if (failures.length) {
      const list = failures.map(cap).join(', ');
      setGlobalError(`Please fix the highlighted days: ${list}.`);
      onChange({ ...value, schedule: next });
      return;
    }

    onChange({ ...value, schedule: next });
    setGlobalError('');
    setBulkFrom(null);
    setBulkTo(null);
  };

  const clearSelected = () => {
    const next = { ...value.schedule };
    selected.forEach(d => {
      next[d] = [];
      setDayError(s => ({ ...s, [d]: '' }));
    });
    setErrorDays(prev => {
      const n = new Set(prev);
      selected.forEach(d => n.delete(d));
      return n;
    });
    onChange({ ...value, schedule: next });
    if (errorDays.size === 0) setGlobalError('');
  };

  // ── UI bits ──
  const BulkBar = (props: any) => (
    <Paper
      elevation={0}
      sx={theme => ({
        background: alpha(theme.palette.primary.main, 0.06),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        borderRadius: 2,
        p: 1.5,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
        mb: 1,
        flexDirection: { xs: 'column', sm: 'row' },
        '& .MuiFormControl-root': { width: { xs: '100%', sm: 'auto' } },
        '& .MuiButton-root': { width: { xs: '100%', sm: 'auto' } },
      })}
      {...props}
    />
  );

  const DayRow = ({ day }: { day: DayKey }) => {
    const pairs = value.schedule[day] || [];
    const disabledAdd = disabled || hasIncompleteRow(pairs) || !!dayError[day];

    return (
      <Box
        height="100%"
        width="100%"
        sx={{
          p: { xs: 1, sm: 1.5 },
          mb: { xs: 1, sm: 1.5 },
          borderRadius: 2,
          border: '1px solid #ddd',
          boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)', // Subtle shadow for depth
          backgroundColor: '#fff', // Clean white background
          transition: 'all 0.3s ease', // Smooth transition on hover
          '&:hover': {
            boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.15)', // Darker shadow on hover
            borderColor: '#bbb', // Slightly darker border on hover
          },
          borderColor: errorDays.has(day) ? 'error.main' : undefined,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Checkbox checked={selected.has(day)} onChange={() => toggleDay(day)} disabled={disabled} size={isMobile ? 'small' : 'medium'} />
            <Typography sx={{ textTransform: 'capitalize', fontWeight: 600 }}>{cap(day)}</Typography>
          </Stack>
        </Stack>

        {/* rows */}
        <Stack spacing={1} sx={{ mt: 1 }}>
          {pairs.length === 0 && (
            <Typography variant="body2" color="text.secondary">No periods yet</Typography>
          )}

          {pairs.map(([start, end], idx) => {
            const fromDate = dateFrom24(start || null);
            const toDate = dateFrom24(end || null);

            return (
              <Box
                key={idx}
                sx={{ 
                  display: 'flex', 
                  flexDirection: "row", 
                  gap: 1, 
                  alignItems: 'center',
                  flexWrap: 'wrap'   
                }}>

                  <IconButton size="small" onClick={() => deleteRow(day, idx)}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <TimePicker
                    ampm 
                    label="From"
                    value={fromDate}
                    onChange={(d) => {
                      const v = [...pairs] as Pair[];
                      v[idx] = [to24FromDate(d), end];
                      setPairs(day, v);
                    }}
                    sx={{maxWidth:isDesktop?135:undefined,}}
                    slotProps={{ textField: { size: 'small', fullWidth: isMobile } }}
                  />
                  <TimePicker
                    ampm 
                    label="To" 
                    value={toDate}
                    sx={{maxWidth:isDesktop?135:undefined}}

                    onChange={(d) => {
                      const v = [...pairs] as Pair[];
                      v[idx] = [start, to24FromDate(d)];
                      setPairs(day, v);
                    }}
                    slotProps={{ textField: { size: 'small', fullWidth: isMobile}}}

                  />
                </LocalizationProvider>

                
              </Box>
            );
          })}
        </Stack>

        {/* per-day add row button */}
        <Stack  direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ mt: 1 }}>

                  <Box sx={{width:"100%", display:"flex", flexDirection:"row", gap:1, alignItems:"center", justifyContent:"center"}}>

                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    size="small"
                    disabled={disabledAdd}
                    onClick={() => addEmptyRow(day)}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  >
                    Add work period
                  </Button>


                  </Box>
          
          {dayError[day] && (
            <Typography variant="body2" color="error">{dayError[day]}</Typography>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <Box>
      {/* Header area: info when nothing selected, controls when selected */}
      {selected.size === 0 ? (
        <Alert severity="info" sx={{ mb: 1, borderRadius: 2 }}>
          Select one or more days to add the same period to all of them at once.
          You can still add periods per day using each day’s “Add work period”.
        </Alert>
      ) : (
        <BulkBar>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <TimePicker
              ampm label="From" value={bulkFrom} onChange={onBulkFromChange}
              slotProps={{ textField: { size: 'small' } }}
            />
            <TimePicker
              ampm label="To" value={bulkTo} onChange={onBulkToChange}
              slotProps={{ textField: { size: 'small' } }}
            />
          </LocalizationProvider>

          <Tooltip title="Add this period to all selected days">
            <span>
              <Button
                variant="contained" size="small" startIcon={<AddIcon />}
                onClick={addToSelected}
                disabled={disabled}
                sx={{ textTransform: 'none', borderRadius: 10 }}
              >
                Add to selected
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="Clear all periods from selected days">
            <span>
              <Button
                variant="outlined" size="small" startIcon={<ClearAllIcon />}
                onClick={clearSelected}
                disabled={disabled}
                sx={{ textTransform: 'none', borderRadius: 10 }}
              >
                Clear selected
              </Button>
            </span>
          </Tooltip>

          <Stack direction="row" alignItems="center" sx={{ ml: 'auto' }}>
            <Chip
              size="small"
              label={`${selected.size} selected`}
              sx={{
                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                color: 'primary.main',
                fontWeight: 600,
              }}
            />
          </Stack>
        </BulkBar>
      )}

      {globalError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setGlobalError('')}>
          {globalError}
        </Alert>
      )}

      <Divider sx={{ mb: 1 }} />

     
        {[0, 3].map((d, index) => <Grid2 
            key={d} 
            container
            spacing={1} 
            justifyContent="stretch" 
            alignItems="stretch"
          >
                <Grid2  display="flex" width="100%" key={d} xs={12} sm={12} md={4} lg={4} >
                   <DayRow day={DAYS[d]} />
                </Grid2>
                <Grid2  display="flex" width="100%" key={d} xs={12} sm={12} md={4} lg={4} >
                   <DayRow day={DAYS[d+1]} />
                </Grid2>
                <Grid2  display="flex" width="100%" key={d} xs={12} sm={12} md={4} lg={4} >
                   <DayRow day={DAYS[d+2]} />
                </Grid2>
          </Grid2>
            )
        }

        <Grid2 
            width="100%"
            height="100%"
            key={"sunday"} 
            container spacing={1} 
            justifyContent="stretch" 
            alignItems="stretch"
          >
              <Grid2  display="flex" height="100%" width="100%" key={"sunday"} xs={12} sm={12} md={4} lg={4} sx={{height:"100%"}}>
                   <DayRow day={DAYS[6]} />
                </Grid2>
          </Grid2>
        
    </Box>
  );
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
